import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, ShieldCheck, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { LoginParticles } from '@/components/login-particles';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

// Login form validation schema
const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

// W1a-15 — context picker types. Mirrors the API's discriminated-union
// /auth/login response.
interface AvailableTenant {
  id: string;
  name: string;
  slug: string;
  /** Null when the person holds no shipped platform role in the tenant. */
  role: string | null;
}
interface AvailableContexts {
  tenants: AvailableTenant[];
  sysadmin: boolean;
}
interface ContextSelectionState {
  contextSelectionToken: string;
  availableContexts: AvailableContexts;
}

/**
 * What an `?sso_error=` reason token means, in words a person can act on
 * (B2d-T4). The server sends a token, never a sentence: the redirect URL is
 * anonymous and logged, so nothing about the provider, the connection or the
 * person may ride on it. The mapping lives here because this is the only
 * surface that renders it.
 */
const SSO_ERROR_MESSAGES: Record<string, string> = {
  connection_unavailable:
    'That single sign-on link is not available. Ask your administrator to check it is still enabled.',
  connection_incomplete:
    'That single sign-on connection is not finished being set up. Ask your administrator to complete it.',
  external_connections_disabled:
    'Single sign-on is unavailable because external connections are turned off for your organization.',
  provider_unreachable:
    'Your identity provider could not be reached. Try again, or sign in with a password.',
  provider_refused:
    'Your identity provider refused the sign-in. Check with your administrator that you are assigned to this application.',
  handshake_missing:
    'That sign-in attempt has expired or was already used. Please start again.',
  deployment_url_unconfigured:
    'Single sign-on is not finished being set up on this server. An administrator must configure the application URL.',
  session_store_unavailable:
    'Single sign-on is temporarily unavailable on this server. Please try again shortly.',
  connection_disabled:
    'That single sign-on connection is turned off. Ask your administrator to enable it.',
  jit_disabled:
    'There is no account here for you yet, and this connection does not create them. Ask your administrator for an invitation.',
  account_not_live:
    'Your account is not active in this workspace. Ask your administrator to restore it.',
  link_requires_verified_email:
    'Your identity provider did not confirm your email address, so it could not be matched to your account.',
  role_key_invalid:
    'That connection is configured with a role this workspace does not have. Ask your administrator to fix it.',
  // SEC-B2D-01. An account holding Owner authority is not reachable through a
  // single sign-on connection — it signs in with its own credentials.
  subject_is_owner:
    'Accounts with Owner authority sign in with a password, not through single sign-on. Use the password form below.',
  default:
    'Single sign-on did not complete. Please try again, or sign in with a password.',
};

function LoginPage() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // W1a-15 — multi-step state: 'credentials' (email+password), then
  // either 'context' (pick tenant or sysadmin) or 'totp' (sysadmin
  // selected, prompt for code).
  const [contextState, setContextState] = useState<ContextSelectionState | null>(
    null
  );
  const [pickedContext, setPickedContext] = useState<
    | { kind: 'tenant'; tenantId: string }
    | { kind: 'sysadmin' }
    | null
  >(null);
  const [totpCode, setTotpCode] = useState('');

  // B2d-T1 — the forced password change. When an administrator has forced a
  // reset, /auth/login answers with NO access token and NO refresh token: just
  // `{ mustChangePassword: true, changePasswordToken }`. There is nothing this
  // page could do with that response except collect a new password, which is
  // the point — the refusal is a deny, not a hint a client is free to ignore.
  //
  // B2dT1-R1 / BUG-01 — `needsCurrentPassword` is CAPTURED WHEN THE STATE IS
  // CREATED and never recomputed. It used to be asked as
  // `passwordChange.currentPassword === ''`, i.e. the field's visibility was a
  // re-read of the field's own value: on the select-context door the state is
  // created with an empty current password on purpose (that door never
  // collected one), so the input rendered, the first keystroke wrote 'A' back
  // into `currentPassword`, the guard turned false and React unmounted the
  // input. The person was left holding a one-character current password with
  // no field to extend it in, the submit button enabled, and five submits away
  // from a 15-minute account lockout. A control's visibility must never be
  // derived from the value it controls.
  const [passwordChange, setPasswordChange] = useState<{
    token: string;
    currentPassword: string;
    needsCurrentPassword: boolean;
  } | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Bug #85 — credential prefill removed from the deployed login form.
  //
  // The previous implementation read VITE_DEV_LOGIN_EMAIL /
  // VITE_DEV_LOGIN_PASSWORD at build time and prefilled the form when
  // `import.meta.env.DEV` was truthy. The ai1 shared dev deployment
  // ships with DEV=true (it IS the dev stack), so the prefill landed
  // on a shared deployment where QA and developers all saw the same
  // admin-style credentials baked into the page. That is a credential-
  // handling and session-confusion risk in a shared environment — a
  // QA tester would land on the form with an admin email already
  // filled in and could not tell whether they were resuming an admin
  // session or starting fresh.
  //
  // The fix: NEVER prefill credentials in the deployed bundle. If a
  // local-only developer wants prefill, they can opt in by setting
  // `VITE_DEV_LOGIN_FORCE_PREFILL=1` AND running on `localhost` or
  // `127.0.0.1`. The DEV-bundle ai1 ships does not set the force
  // flag, so the form is always blank for QA / shared use.
  const isLocalhost =
    typeof window !== 'undefined' &&
    /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
  const forcePrefill = import.meta.env.VITE_DEV_LOGIN_FORCE_PREFILL === '1';
  const devEmail =
    import.meta.env.DEV && isLocalhost && forcePrefill
      ? (import.meta.env.VITE_DEV_LOGIN_EMAIL as string | undefined) ?? ''
      : '';
  const devPassword =
    import.meta.env.DEV && isLocalhost && forcePrefill
      ? (import.meta.env.VITE_DEV_LOGIN_PASSWORD as string | undefined) ?? ''
      : '';

  // Bug #86 — surface validation feedback on the FIRST submit.
  //
  // The default `mode: 'onSubmit'` + `reValidateMode: 'onChange'` is
  // already correct for showing field-level errors after the user
  // clicks Log In with an empty / invalid value, so the schema-
  // derived messages ("Invalid email address", "Password is required")
  // should appear next to the field via <FormMessage />. They DID
  // appear in the DOM in QA but the dark login card's
  // `text-destructive` style rendered them at low contrast against
  // the dark background, and the QA evidence DOM-scrape missed
  // them because no `role="alert"` was set.
  //
  // The explicit `mode/reValidateMode` here documents the intent so
  // a future refactor can't silently drop validation feedback.
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      email: devEmail,
      password: devPassword,
    },
  });

  // Bug #85 — redirect authenticated users away from /login. Direct
  // navigation to /login while already authenticated previously
  // re-rendered the credential form (with the now-removed prefill),
  // which confused users into thinking their session had expired.
  // Detect a live access token and bounce to the dashboard. The
  // sysadmin / tenant split is read from the audience claim so
  // platform admins land on /sysadmin, tenant users on /.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (typeof payload?.exp === 'number' && payload.exp * 1000 < Date.now()) {
        // Expired — clear and stay on /login.
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('authContext');
        return;
      }
      const isSysadminToken = payload?.aud === 'argusai-admin';
      navigate({
        to: isSysadminToken ? ('/sysadmin' as never) : '/',
        replace: true,
      });
    } catch {
      // Malformed token — purge it so /login renders cleanly.
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('authContext');
    }
    // navigate is stable; only re-run on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Simulate initial loading (for branding fetch in production)
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 300);
    return () => clearTimeout(timer);
  }, []);

  // B2d-T4 — FINISH AN SSO SIGN-IN.
  //
  // The IdP redirects the person to the API's callback, which signs them in and
  // sends them HERE with a one-time code — never with the tokens themselves,
  // which would write a live credential into browser history and the next
  // request's Referer. The code buys exactly one token pair at
  // POST /auth/sso/exchange and is deleted as it is read.
  //
  // `sso_error` carries a REASON TOKEN and nothing else (no provider text, no
  // email), because this URL is anonymous and logged by everything on the way.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const ssoError = params.get('sso_error');
    const ssoCode = params.get('sso_code');
    if (!ssoError && !ssoCode) return;

    // Take the parameter out of the address bar either way: a code is spent
    // and an error should not survive a refresh.
    window.history.replaceState({}, '', window.location.pathname);

    if (ssoError) {
      setFormError(SSO_ERROR_MESSAGES[ssoError] ?? SSO_ERROR_MESSAGES.default);
      setIsLoading(false);
      return;
    }

    void (async () => {
      try {
        const res = await fetch('/api/v1/auth/sso/exchange', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: ssoCode }),
        });
        if (!res.ok) {
          setFormError(SSO_ERROR_MESSAGES.default);
          setIsLoading(false);
          return;
        }
        const data = (await res.json()) as {
          accessToken: string;
          refreshToken: string;
        };
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem('authContext', 'tenant');
        navigate({ to: '/' });
      } catch {
        setFormError(SSO_ERROR_MESSAGES.default);
        setIsLoading(false);
      }
    })();
    // navigate is stable; only re-run on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (values: LoginFormValues) => {
    setIsSubmitting(true);
    setFormError(null);

    try {
      const response = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });

      const data = await response.json();

      if (!response.ok) {
        setFormError(data.error?.message || 'Invalid email or password');
        setIsSubmitting(false);
        return;
      }

      // B2d-T1 — the account must change its password before it may hold a
      // session. Nothing is stored: there is no token to store.
      if (data.mustChangePassword) {
        setPasswordChange({
          token: data.changePasswordToken,
          currentPassword: values.password,
          // This door DID collect it — the person just typed it.
          needsCurrentPassword: false,
        });
        setNewPassword('');
        setConfirmPassword('');
        setIsSubmitting(false);
        return;
      }

      // W1a-15 — discriminated response. Multi-context callers must
      // pick which surface (tenant or sysadmin) before we can issue a
      // real access token.
      if (data.requiresContextSelection) {
        const ctx: ContextSelectionState = {
          contextSelectionToken: data.contextSelectionToken,
          availableContexts: data.availableContexts,
        };

        // Auto-select if there's only one possible context (e.g.
        // sysadmin-only user, or 1 tenant + no sysadmin — though that
        // case shouldn't reach here).
        const onlyTenant =
          ctx.availableContexts.tenants.length === 1 &&
          !ctx.availableContexts.sysadmin
            ? ctx.availableContexts.tenants[0]
            : null;
        const onlySysadmin =
          ctx.availableContexts.tenants.length === 0 &&
          ctx.availableContexts.sysadmin;

        if (onlyTenant) {
          // One tenant, no sysadmin — auto-pick the tenant.
          setContextState(ctx);
          await submitContextSelection(ctx, {
            kind: 'tenant',
            tenantId: onlyTenant.id,
          });
          return;
        }
        if (onlySysadmin) {
          // Sysadmin-only — go straight to TOTP step.
          setContextState(ctx);
          setPickedContext({ kind: 'sysadmin' });
          setIsSubmitting(false);
          return;
        }

        // Multiple options — show the picker.
        setContextState(ctx);
        setIsSubmitting(false);
        return;
      }

      // Single-tenant, no-sysadmin path — existing shape.
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('authContext', 'tenant');

      // Navigate to dashboard
      navigate({ to: '/' });
    } catch {
      setFormError('Network error. Please try again.');
      setIsSubmitting(false);
    }
  };

  /**
   * W1a-15 — submit the picked context to /auth/login/select-context.
   * Returns access + refresh tokens; for sysadmin context, redirects to
   * /admin/platform; for tenant context, redirects to /.
   */
  const submitContextSelection = async (
    ctx: ContextSelectionState,
    pick:
      | { kind: 'tenant'; tenantId: string }
      | { kind: 'sysadmin' }
  ) => {
    setIsSubmitting(true);
    setFormError(null);

    try {
      const body: Record<string, string> = {
        contextSelectionToken: ctx.contextSelectionToken,
        context: pick.kind,
      };
      if (pick.kind === 'tenant') body.tenantId = pick.tenantId;
      if (pick.kind === 'sysadmin') body.totpCode = totpCode;

      const response = await fetch('/api/v1/auth/login/select-context', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (!response.ok) {
        // Discriminated 401 codes — translate to user-facing copy.
        const code = data.error?.code as string | undefined;
        const fallback =
          code === 'TOTP_REQUIRED'
            ? 'Enter your authenticator code.'
            : code === 'TOTP_INVALID'
              ? 'Authenticator code is invalid.'
              : code === 'NOT_SYSADMIN'
                ? 'You do not have a sysadmin role on this platform.'
                : code === 'NOT_A_MEMBER'
                  ? 'You are not a member of the requested workspace.'
                  : code === 'CONTEXT_TOKEN_INVALID'
                    ? 'Your sign-in session has expired. Please log in again.'
                    : 'Failed to complete sign-in.';
        setFormError(data.error?.message || fallback);

        // Reset to credentials step if the context token expired.
        if (code === 'CONTEXT_TOKEN_INVALID') {
          setContextState(null);
          setPickedContext(null);
          setTotpCode('');
        }

        setIsSubmitting(false);
        return;
      }

      // B2d-T1 — the flag is read at this door too (a context-selection token
      // stays valid for five minutes, and a reset forced inside that window
      // must not be spendable for a session).
      if (data.mustChangePassword) {
        setContextState(null);
        setPickedContext(null);
        setTotpCode('');
        setPasswordChange({
          token: data.changePasswordToken,
          currentPassword: '',
          // This door did NOT collect it: the context picker holds a
          // context-selection token, not a password. The field is asked for,
          // and the flag says so ONCE — it is never re-derived from the value
          // the field writes (B2dT1-R1).
          needsCurrentPassword: true,
        });
        setNewPassword('');
        setConfirmPassword('');
        setIsSubmitting(false);
        return;
      }

      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('authContext', data.context);

      // Sysadmin → platform admin. Tenant → dashboard.
      if (data.context === 'sysadmin') {
        navigate({ to: '/sysadmin' as never });
      } else {
        navigate({ to: '/' });
      }
    } catch {
      setFormError('Network error. Please try again.');
      setIsSubmitting(false);
    }
  };

  /**
   * B2d-T1 — spend the password-change token at POST /auth/change-password.
   * On success the server mints the session it withheld: either the token
   * pair, or the context picker for a multi-tenant or sysadmin person, which
   * this page then walks exactly as it does after a plain login.
   */
  const submitPasswordChange = async () => {
    if (!passwordChange) return;
    if (newPassword !== confirmPassword) {
      setFormError('The two passwords do not match.');
      return;
    }
    setIsSubmitting(true);
    setFormError(null);

    try {
      const response = await fetch('/api/v1/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          changePasswordToken: passwordChange.token,
          currentPassword: passwordChange.currentPassword,
          newPassword,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        const code = data.error?.code as string | undefined;
        setFormError(
          data.error?.message ||
            (code === 'PASSWORD_CHANGE_TOKEN_INVALID'
              ? 'This password-change session has expired. Please sign in again.'
              : 'Could not change your password.'),
        );
        if (
          code === 'PASSWORD_CHANGE_TOKEN_INVALID' ||
          code === 'PASSWORD_CHANGE_NOT_REQUIRED'
        ) {
          setPasswordChange(null);
        }
        setIsSubmitting(false);
        return;
      }

      setPasswordChange(null);
      setNewPassword('');
      setConfirmPassword('');

      if (data.requiresContextSelection) {
        setContextState({
          contextSelectionToken: data.contextSelectionToken,
          availableContexts: data.availableContexts,
        });
        setIsSubmitting(false);
        return;
      }

      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('authContext', 'tenant');
      navigate({ to: '/' });
    } catch {
      setFormError('Network error. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleSSOLogin = () => {
    // Redirect to SSO discovery endpoint
    window.location.href = '/api/v1/sso/discover';
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f172a]">
        <svg
          className="animate-spin h-10 w-10 text-primary"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full relative overflow-hidden bg-[#0f172a] dark">
      {/* Animated particle/links background (canvas — crash-safe; see
          components/login-particles.tsx). Sits behind the z-10 card. */}
      <LoginParticles />
      {/* Login Card — forced dark styling for the login page */}
      <div className="min-h-screen flex items-center justify-center p-4 relative z-10">
        <Card
          className="w-full max-w-md shadow-2xl bg-[#0f172a]/90 backdrop-blur-sm border-slate-700/50 text-slate-100"
          style={{
            // Force dark colors on the login card regardless of branding theme
            '--color-foreground': '210 40% 92%',
            '--color-background': '222 47% 5%',
            '--color-card': '222 47% 11%',
            '--color-card-foreground': '210 40% 92%',
            '--color-border': '215 25% 30%',
            '--color-input': '215 25% 30%',
            '--color-muted-foreground': '215 20% 60%',
            '--color-popover': '222 47% 11%',
            '--color-popover-foreground': '210 40% 92%',
          } as React.CSSProperties}
        >
          <CardHeader className="text-center pt-8 pb-4">
            {/* ArgusAI Brand — icon + text (not the IQ wordmark) */}
            <div className="flex flex-col items-center gap-3">
              <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                <img
                  src="/ArgusLogo_500px-Logo.png"
                  alt="ArgusAI"
                  className="w-14 h-14 object-contain logo-purple-tint"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white tracking-tight">
                  Argus<span className="text-primary">AI</span><sup className="text-[10px] ml-0.5 text-slate-400">™</sup>
                </h1>
                <p className="text-xs text-slate-400 mt-0.5">Enterprise AI Platform</p>
              </div>
            </div>
            <p className="text-sm text-slate-400 mt-4">
              {passwordChange
                ? 'Set a new password to continue'
                : contextState && pickedContext?.kind === 'sysadmin'
                  ? 'Sysadmin sign-in — enter your authenticator code'
                  : contextState
                    ? 'Choose where to sign in'
                    : 'Sign in to your account'}
            </p>
          </CardHeader>

          <CardContent>
            {/* W1a-15 — three-step flow: credentials → context picker → TOTP.
                B2d-T1 inserts a fourth, ahead of all of them: a forced
                password change, which is the only thing the server will let
                this person do. */}
            {passwordChange ? (
              // ─── STEP 0: Forced password change ────────────────────────
              <div className="space-y-6">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    submitPasswordChange();
                  }}
                  className="space-y-6"
                >
                  <p className="text-xs text-slate-400">
                    An administrator has required a new password on this
                    account. Your previous sessions have ended.
                  </p>

                  {/* The current password is re-asked only when this page did
                      not just collect it (the context-picker door). The guard
                      is the CAPTURED flag, never `currentPassword === ''` —
                      that read unmounted the field on its own first keystroke
                      (B2dT1-R1 / BUG-01). */}
                  {passwordChange.needsCurrentPassword && (
                    <div className="space-y-2">
                      <label
                        htmlFor="current-password-input"
                        className="text-sm font-medium text-slate-200"
                      >
                        Current password
                      </label>
                      <Input
                        id="current-password-input"
                        type="password"
                        autoComplete="current-password"
                        value={passwordChange.currentPassword}
                        onChange={(e) =>
                          setPasswordChange({
                            ...passwordChange,
                            currentPassword: e.target.value,
                          })
                        }
                        disabled={isSubmitting}
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <label
                      htmlFor="new-password-input"
                      className="text-sm font-medium text-slate-200"
                    >
                      New password
                    </label>
                    <Input
                      id="new-password-input"
                      type="password"
                      autoFocus
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="confirm-password-input"
                      className="text-sm font-medium text-slate-200"
                    >
                      Confirm new password
                    </label>
                    <Input
                      id="confirm-password-input"
                      type="password"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>

                  {formError && (
                    <div
                      role="alert"
                      aria-live="assertive"
                      className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm font-medium text-red-300"
                    >
                      {formError}
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={
                      isSubmitting ||
                      newPassword.length === 0 ||
                      confirmPassword.length === 0 ||
                      passwordChange.currentPassword.length === 0
                    }
                  >
                    {isSubmitting ? 'Saving...' : 'Set password and sign in'}
                  </Button>
                </form>
              </div>
            ) : contextState && pickedContext?.kind === 'sysadmin' ? (
              // ─── STEP 3: Sysadmin TOTP ─────────────────────────────────
              <div className="space-y-6">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (contextState && pickedContext) {
                      submitContextSelection(contextState, pickedContext);
                    }
                  }}
                  className="space-y-6"
                >
                  <div className="space-y-2">
                    <label
                      htmlFor="totp-input"
                      className="text-sm font-medium text-slate-200"
                    >
                      6-digit authenticator code
                    </label>
                    <Input
                      id="totp-input"
                      type="text"
                      inputMode="numeric"
                      pattern="\d{6}"
                      maxLength={6}
                      autoFocus
                      autoComplete="one-time-code"
                      placeholder="000000"
                      value={totpCode}
                      onChange={(e) =>
                        setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                      }
                      disabled={isSubmitting}
                      className="text-center text-lg tracking-widest font-mono"
                    />
                    <p className="text-xs text-slate-500">
                      Open your authenticator app and enter the current ArgusAI code.
                    </p>
                  </div>

                  {/* Bug #86 — bright-on-dark TOTP error banner. */}
                  {formError && (
                    <div
                      role="alert"
                      aria-live="assertive"
                      className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm font-medium text-red-300"
                    >
                      {formError}
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isSubmitting || totpCode.length !== 6}
                  >
                    {isSubmitting ? 'Verifying...' : 'Sign in as sysadmin'}
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full text-slate-400 hover:text-slate-100"
                    disabled={isSubmitting}
                    onClick={() => {
                      // Back to context picker. Keep contextSelectionToken
                      // alive — it's still valid for the rest of its TTL.
                      setPickedContext(null);
                      setTotpCode('');
                      setFormError(null);
                    }}
                  >
                    ← Back
                  </Button>
                </form>
              </div>
            ) : contextState ? (
              // ─── STEP 2: Context picker ────────────────────────────────
              <div className="space-y-3">
                {contextState.availableContexts.tenants.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      const pick = { kind: 'tenant' as const, tenantId: t.id };
                      setPickedContext(pick);
                      submitContextSelection(contextState, pick);
                    }}
                    disabled={isSubmitting}
                    className="w-full p-4 rounded-lg border border-slate-700 hover:border-primary/60 hover:bg-slate-800/50 text-left transition-colors flex items-center gap-3 disabled:opacity-50"
                  >
                    <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-100 truncate">
                        {t.name}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {t.role ? `${t.slug} · ${t.role}` : t.slug}
                      </p>
                    </div>
                  </button>
                ))}

                {contextState.availableContexts.sysadmin && (
                  <button
                    type="button"
                    onClick={() => {
                      setPickedContext({ kind: 'sysadmin' });
                      setTotpCode('');
                      setFormError(null);
                    }}
                    disabled={isSubmitting}
                    className="w-full p-4 rounded-lg border border-amber-700/50 hover:border-amber-500 hover:bg-amber-950/20 text-left transition-colors flex items-center gap-3 disabled:opacity-50"
                  >
                    <div className="w-10 h-10 rounded-md bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                      <ShieldCheck className="h-5 w-5 text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-amber-200">
                        Sysadmin Console
                      </p>
                      <p className="text-xs text-slate-500">
                        Platform administration · authenticator required
                      </p>
                    </div>
                  </button>
                )}

                {/* Bug #86 — bright-on-dark context-picker error banner. */}
                {formError && (
                  <div
                    role="alert"
                    aria-live="assertive"
                    className="mt-2 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm font-medium text-red-300"
                  >
                    {formError}
                  </div>
                )}

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-slate-400 hover:text-slate-100 mt-2"
                  disabled={isSubmitting}
                  onClick={() => {
                    setContextState(null);
                    setPickedContext(null);
                    setTotpCode('');
                    setFormError(null);
                  }}
                >
                  ← Back
                </Button>
              </div>
            ) : (
              // ─── STEP 1: Credentials ───────────────────────────────────
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g., admin@argus.io"
                          type="email"
                          autoComplete="email"
                          disabled={isSubmitting}
                          className={cn(fieldState.error && 'border-destructive')}
                          {...field}
                        />
                      </FormControl>
                      {/* Bug #86 — explicit role="alert" + bright-on-dark
                          color so screen readers and our dark login card
                          both surface zod field errors clearly. */}
                      <FormMessage
                        role="alert"
                        className="text-red-400"
                      />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            placeholder="password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            disabled={isSubmitting}
                            className={cn(fieldState.error && 'border-destructive')}
                            {...field}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      {/* Bug #86 — explicit role="alert" + bright-on-dark
                          color so screen readers and our dark login card
                          both surface zod field errors clearly. */}
                      <FormMessage
                        role="alert"
                        className="text-red-400"
                      />
                    </FormItem>
                  )}
                />

                {/* Bug #86 — form-level error has role="alert", aria-live,
                    and a bright-on-dark style so it is unmistakably
                    surfaced after a failed POST /auth/login (wrong
                    credentials, network failure, rate-limit, etc.). */}
                {formError && (
                  <div
                    role="alert"
                    aria-live="assertive"
                    className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm font-medium text-red-300"
                  >
                    {formError}
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? 'Logging in...' : 'Log In'}
                </Button>
              </form>
            </Form>
            )}

            {/* Divider — only shown on the credentials step */}
            {!contextState && (<>
            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full border-slate-700" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-[#0f172a]/90 px-2 text-slate-500">Or</span>
              </div>
            </div>

            {/* SSO Button */}
            <Button variant="outline" className="w-full border-slate-600 text-slate-300 hover:bg-slate-800" onClick={handleSSOLogin}>
              Sign in with your Organization
            </Button>
            </>)}

            {/* Footer Branding */}
            <div className="mt-8 pt-6 border-t border-slate-700/50 text-center text-xs text-slate-500">
              <p className="font-semibold text-primary/80">ArgusAI</p>
              <p className="mt-0.5">powered by Viaanix Inc.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
